import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import { ReactComponent as ScheduledCreative } from "../../../../../assets/icons/svg/Scheduled-Creative.svg";
import { ReactComponent as All } from "../../../../../assets/icons/svg/all.svg";
import { ReactComponent as Landscape } from "../../../../../assets/icons/svg/landscape.svg";
import { ReactComponent as Protrait } from "../../../../../assets/icons/svg/Protrait.svg";
import { usePlaylist } from "../../../../../context/PlaylistContext";
import CustomButton from "../../../../ui/CommonButton";
import { useTopBar } from "../../../../../context/TopBarContext";
import { useCreatives } from "../../../../../context/CreativeContext";

// Type definitions
type FilterType = "all" | "landscape" | "portrait";

// Multi-select Creative Selection Card Component
const MultiSelectCreativeCard: React.FC<{
  creative: any;
  isSelected: boolean;
  isExisting: boolean;
  onSelect: () => void;
}> = ({ creative, isSelected, isExisting, onSelect }) => {
  const [imageError, setImageError] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const getDisplayFormat = () => {
    if (creative.imageUrl?.startsWith("data:")) {
      const match = creative.imageUrl.match(/^data:(image\/[a-zA-Z]+);base64,/);
      return match ? match[1] : "unknown";
    }

    if (creative.fileFormat) {
      return `image/${creative.fileFormat.toLowerCase()}`;
    }

    if (creative.imageUrl) {
      const urlParts = creative.imageUrl.split(".");
      const extension = urlParts[urlParts.length - 1]
        .split("?")[0]
        .toLowerCase();
      return `image/${extension}`;
    }

    return "unknown";
  };

  return (
    <div
      className={`w-full h-[250px] 2xl:h-[260px] 3xl:h-[260px] 4xl:h-[250px] 5xl:h-[350px] bg-white rounded-[16px] shadow-md overflow-hidden transition-all cursor-pointer relative ${
        isSelected
          ? "shadow-[0_4px_20px_rgba(0,0,0,0.15)] border-2 border-[#0BA6C7]"
          : "shadow-[0_2px_10px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.1)]"
      }`}
      onClick={onSelect}
    >
      {/* Selection Indicator */}
      <div
        className={`absolute top-2 left-2 w-6 h-6 rounded-[6px] flex items-center justify-center z-10 transition 
       ${
         isSelected
           ? "bg-[#0091AF] border border-[#0091AF]"
           : "bg-white border border-[#6B6C73]"
       }`}
      >
        {isSelected && (
          <svg
            className="w-4 h-4 text-white font-semibold"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={4}
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
      </div>

      {/* Already in playlist indicator */}
      {isExisting && (
        <div className="absolute top-2 right-2 bg-[#10B981] text-white text-xs px-2 py-1 rounded-md font-medium z-10">
          In Playlist
        </div>
      )}

      <div className="relative w-full h-[180px] 2xl:h-[190px] 3xl:h-[190px] 4xl:h-[180px] 5xl:h-[280px] bg-gradient-to-br from-[#0BA6C7]/10 to-[#0BA6C7]/20 rounded-t-[16px] flex items-center justify-center overflow-hidden">
        {!imageError ? (
          <img
            src={creative.imageUrl}
            alt="Creative Thumbnail"
            className="max-w-full max-h-full object-contain transition-opacity duration-300"
            onError={handleImageError}
            style={{
              filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.1))",
            }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-gray-400">
            <svg
              className="w-12 h-12 mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z"
              />
            </svg>
            <span className="text-xs">Image not available</span>
          </div>
        )}
      </div>

      <div className="px-4 py-3">
        <div
          className="text-black text-base font-semibold mb-2 truncate"
          title={creative.title}
        >
          {creative.title}
        </div>
        <div className="text-black text-xs font-medium truncate">
          {getDisplayFormat()}
        </div>
      </div>
    </div>
  );
};

const ContentSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { getPlaylistById, updatePlaylist, updatePlaylistCreatives } = usePlaylist();
  const { creatives, allCreatives } = useCreatives();
  const { setTitle, setShowBackButton, pushNavState } = useTopBar();

  const [selectedCreatives, setSelectedCreatives] = useState<string[]>([]);
  const [existingCreatives, setExistingCreatives] = useState<string[]>([]);
  const [filter, setFilter] = useState<FilterType>("all");
  const [search, setSearch] = useState("");
  const [isNewPlaylist, setIsNewPlaylist] = useState(false);

  const playlist = id ? getPlaylistById(id) : null;

  useEffect(() => {
    if (location.state?.isNewPlaylist) {
      setIsNewPlaylist(true);
    }

    if (playlist) {
      setTitle("Add content to playlist");
      setShowBackButton(true);
      pushNavState({
        path: `/playlists/playlist/${id}/add-content`,
        title: "Add content to playlist",
        showBackButton: true,
      });
    }
  }, [playlist, id, location.state, setTitle, setShowBackButton, pushNavState]);

  // Pre-select existing creatives when the page loads
  useEffect(() => {
    if (location.state?.currentSelectedCreatives) {
      const existingCreativeIds =
        location.state.currentSelectedCreatives.filter(
          (creativeId: string) =>
            // Only include if creative still exists and hasn't been deleted
            creatives.some((creative) => creative.id === creativeId) &&
            !location.state?.deletedCreatives?.includes(creativeId)
        );

      setExistingCreatives(existingCreativeIds);
      setSelectedCreatives(existingCreativeIds);
      console.log("Pre-selected existing creatives:", existingCreativeIds);
    }
  }, [
    location.state?.currentSelectedCreatives,
    location.state?.deletedCreatives,
    creatives,
  ]);

  // Filter creatives based on search and filter criteria
  const filteredCreatives = creatives.filter((creative) => {
    const matchesSearch = creative.title
      ?.toLowerCase()
      .includes(search.toLowerCase());
    const matchesFilter =
      filter === "all" ||
      (filter === "landscape" && creative.orientation === "landscape") ||
      (filter === "portrait" && creative.orientation === "portrait");
    return matchesSearch && matchesFilter;
  });

  const handleCreativeSelect = (creativeId: string) => {
    setSelectedCreatives((prev) => {
      if (prev.includes(creativeId)) {
        return prev.filter((id) => id !== creativeId);
      } else {
        return [...prev, creativeId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedCreatives.length === filteredCreatives.length) {
      setSelectedCreatives([]);
    } else {
      setSelectedCreatives(filteredCreatives.map((creative) => creative.id));
    }
  };

  // CRITICAL FIX: Enhanced handleNext function to properly update playlist
  const handleNext = () => {
    if (selectedCreatives.length === 0) {
      alert("Please select at least one creative to continue.");
      return;
    }

    if (playlist) {
      console.log("=== CONTENT SELECTION: Updating playlist ===");
      console.log("Selected creatives:", selectedCreatives);
      console.log("Playlist ID:", playlist.id);

      // CRITICAL: Create creative durations for selected creatives
      const creativeDurations: Record<string, number> = {};
      selectedCreatives.forEach(creativeId => {
        const creative = allCreatives?.find(c => c.id === creativeId);
        if (creative) {
          creativeDurations[creativeId] = creative.duration || 10; // Default 10 seconds
        }
      });

      console.log("Creative durations:", creativeDurations);

      // CRITICAL FIX: Update the playlist's creative data immediately
      updatePlaylistCreatives(
        playlist.id,
        selectedCreatives,
        creativeDurations,
        allCreatives || []
      );

      // CRITICAL FIX: Also update the playlist metadata
      updatePlaylist(playlist.id, {
        creativesCount: selectedCreatives.length,
        status: "Draft Saved" as const,
      });

      console.log("=== NAVIGATING BACK TO PLAYLIST ===");

      // Navigate back to the playlist page with enhanced state data
      navigate(`/playlists/playlist/${playlist.id}`, {
        state: {
          isNewPlaylist: isNewPlaylist,
          // CRITICAL: Pass all selected creatives as newSelectedCreatives
          newSelectedCreatives: selectedCreatives,
          // Pass creative durations for proper display
          creativeDurations: creativeDurations,
          showAddedToast: true,
          // Add timestamp to ensure state changes are detected
          navigationTimestamp: Date.now(),
          // Mark this as coming from content selection
          fromContentSelection: true,
        },
        replace: true, // Use replace to prevent back button issues
      });
    }
  };

  const handleBack = () => {
    if (playlist) {
      navigate(`/playlists/playlist/${playlist.id}`, {
        state: {
          isNewPlaylist: isNewPlaylist,
        },
      });
    }
  };

  if (!playlist) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-gray-500">Playlist not found</p>
          <button
            onClick={() => navigate("/playlists/list")}
            className="mt-4 px-4 py-2 bg-[#0BA6C7] text-white rounded-lg hover:bg-[#0A99B9]"
          >
            Go Back to Playlists
          </button>
        </div>
      </div>
    );
  }

  // Calculate new selections (not including existing ones)
  const newSelections = selectedCreatives.filter(
    (id) => !existingCreatives.includes(id)
  );

  return (
    <div className="min-h-screen bg-white py-4 pl-6 pr-2 mt-1">
      <div className="mx-auto">
        {/* Top Bar - matching CreativesListScreen layout */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-base 2xl:text-xl 4xl:text-2xl font-medium text-gray-900">
            Uploaded {filteredCreatives.length} creatives
            {existingCreatives.length > 0 && (
              <span className="text-sm font-normal text-gray-600 ml-2">
                ({existingCreatives.length} already in playlist)
              </span>
            )}
          </h2>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 3xl:w-6 3xl:h-6" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search content"
                className="w-64 pl-10 pr-4 3xl:pl-12 py-2.5 rounded-[8px] border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-sm placeholder:font-medium"
              />
            </div>

            <div className="flex gap-1 ml-1 text-gray-600">
              <div
                className={`p-2 rounded-md cursor-pointer ${
                  filter === "all"
                    ? "bg-[#F2FAFD] text-[#0090AD]"
                    : "hover:text-black"
                }`}
                onClick={() => setFilter("all")}
              >
                <All className="w-5 h-5" />
              </div>
              <div
                className={`p-2 rounded-md cursor-pointer ${
                  filter === "landscape"
                    ? "bg-[#F2FAFD] text-[#0090AD]"
                    : "hover:text-black"
                }`}
                onClick={() => setFilter("landscape")}
              >
                <Landscape className="w-5 h-5" />
              </div>
              <div
                className={`p-2 rounded-md cursor-pointer ${
                  filter === "portrait"
                    ? "bg-[#F2FAFD] text-[#0090AD]"
                    : "hover:text-black"
                }`}
                onClick={() => setFilter("portrait")}
              >
                <Protrait className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid - Updated responsive grid to match CreativesListScreen */}
        {filteredCreatives.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="text-gray-400 text-center">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <ScheduledCreative className="w-44 h-44 text-gray-500 -mb-3" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No creatives found
              </h3>
              <p className="text-gray-500">
                Try adjusting your search or filters
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-4 3xl:grid-cols-4 4xl:grid-cols-5 gap-6 justify-items-center pb-32">
            {filteredCreatives.map((creative) => (
              <MultiSelectCreativeCard
                key={creative.id}
                creative={creative}
                isSelected={selectedCreatives.includes(creative.id)}
                isExisting={existingCreatives.includes(creative.id)}
                onSelect={() => handleCreativeSelect(creative.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Fixed Footer - Properly positioned at bottom of screen */}
      <div className="fixed bottom-0 right-0 left-[56px] md:left-[180px] xl:left-[190px] 2xl:left-[250px] border-t border-gray-200 bg-white px-4  4xl:px-6 5xl:px-8 py-2 2xl:py-3 4xl:py-4 5xl:py-6 z-50 shadow-lg">
        <div className="flex justify-between items-center mx-auto">
          <div className="text-base 2xl:text-xl 4xl:text-2xl font-medium text-[#1B1D21]">
            {selectedCreatives.length === 0 ? (
              <>
                Select creatives for{" "}
                <span className="italic">"{playlist.title}"</span>
              </>
            ) : (
              <>
                Selected {selectedCreatives.length} creative
                {selectedCreatives.length !== 1 ? "s" : ""}
                {newSelections.length > 0 &&
                  newSelections.length !== selectedCreatives.length && (
                    <span className="text-sm font-normal text-gray-600 ml-1">
                      ({newSelections.length} new)
                    </span>
                  )}{" "}
                for <span className="italic">"{playlist.title}"</span>
              </>
            )}
          </div>
          <div className="flex gap-3">
            <CustomButton
              variant="outline"
              onClick={handleBack}
              className="px-6 py-2"
            >
              Previous
            </CustomButton>
            <CustomButton
              variant="default"
              isActive={selectedCreatives.length > 0}
              onClick={handleNext}
              disabled={selectedCreatives.length === 0}
              className="px-6 py-2"
            >
              {newSelections.length > 0 ? (
                <>Next ({newSelections.length} new)</>
              ) : (
                <>Next ({selectedCreatives.length})</>
              )}
            </CustomButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentSelectionPage;